import React, { useCallback, useMemo } from 'react';
import { documentApi } from 'api/main';
import { texts } from 'utils/languages/fr';

import { CustomFormLabel, FileInputButton, FileErrorMessage, InfoButton } from 'components';
import { Box, FormControl, OutlinedInput } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    input: {
        [theme.breakpoints.down('375')]: {
            marginRight: 0,
            marginBottom: 5,
        },
    },
    docName: {
        '&:hover': {
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    },
    docInputGroup: {
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('375')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    chooseDocButton: {
        marginBottom: '0 !important',
    },
}));

const DocumentInput = ({
    name,
    doc,
    label,
    loading,
    onDocChange,
    fileSizeError,
    error,
    onlyImage,
    infoButtonMessage = null,
}) => {
    const classes = useStyles();

    const docName = useMemo(() => doc?.name || '', [doc]);
    const docId = useMemo(() => doc?.id || '', [doc]);
    const mimeType = useMemo(() => doc?.mimeType || '', [doc]);

    const downloadClick = useCallback(
        url => {
            const link = document.createElement('a');
            if (
                mimeType !==
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document' &&
                mimeType !== 'application/vnd.openxmlformats-officedocument.word'
            ) {
                link.target = '_blank';
            } else {
                link.download = docName;
            }
            link.href = url;
            link.click();
        },
        [docName, mimeType]
    );

    const handleDownload = useCallback(() => {
        if (docId) {
            documentApi
                .downloadDocumentById(docId)
                .then(response => {
                    const bytes = new Uint8Array(response.data);
                    const blob = new Blob([bytes], { type: mimeType });
                    const url = window.URL.createObjectURL(blob);
                    downloadClick(url);
                })
                .catch(() => {
                    console.log('error downloading document');
                });
        }
    }, [docId, downloadClick, mimeType]);

    return (
        <FormControl error={!!error}>
            {label && <CustomFormLabel>{label}</CustomFormLabel>}
            <div className={classes.docInputGroup}>
                <Box
                    display="flex"
                    flexDirection="row"
                    flexWrap="nowrap"
                    flexGrow="1"
                    gap={2}
                    justifyContent="space-between"
                >
                    <OutlinedInput
                        classes={{
                            root: classes.input,
                            input: classes.docName,
                        }}
                        fullWidth
                        disabled
                        value={docName}
                        error={error}
                        onClick={handleDownload}
                    />
                    {infoButtonMessage && (
                        <InfoButton disableMarginBottom title={label} content={infoButtonMessage} />
                    )}
                </Box>
                <FileInputButton
                    id={name}
                    loading={loading}
                    label={texts.UPLOAD_FILE_LABEL}
                    onFileChange={onDocChange}
                    buttonClassName={classes.chooseDocButton}
                    acceptedFileTypes={
                        onlyImage ? '.jpeg, .jpg, .png' : '.pdf, .docx, .jpeg, .jpg, .png'
                    }
                />
                {fileSizeError === name && <FileErrorMessage variant="size" />}
            </div>
        </FormControl>
    );
};

export default DocumentInput;
